import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarCheck, LuCalendarClock, LuCalendarPlus, LuCircleCheck, LuListTodo, LuPencil, LuRotateCcw, LuSparkles, LuTrash2, LuUserPen, LuWallet } from 'react-icons/lu';
import DetailPane from '../../../components/data/DetailPane';
import useTodoText from '../todoText';
import { isOverdue } from '../useTodoList';
import { OverdueBadge, PriorityBadge, StatusBadge, StatusChooser, statusMeta } from './taskBits';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';
const TAG = 'tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset';

/**
 * The selected task's panel (replaces the old detail window and the row
 * buttons): description, assignees, status (for the assignees and admins),
 * dates and, for admins, edit / delete. Docked beside the table on wide
 * screens, a drawer otherwise.
 */
const TaskDetailPane = ({ docked, height, task, isAdmin, canChange, savingStatus, position, onPrev, onNext, onClose, onStatusChange, onEdit, onDelete }) => {
  const { label, formatDate, formatDateTime, creatorLabel } = useTodoText();
  const paneLabel = label('panel.label');

  if (!task) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const overdue = isOverdue(task);
  const completed = task.status === 'completed';
  const assignees = task.assignees && task.assignees.length > 0
    ? task.assignees.map((assignee) => assignee.name)
    : (task.assigneeName ? [task.assigneeName] : []);

  const details = [
    { key: 'dueDate', icon: LuCalendarClock, label: label('panel.dueDate'), value: formatDate(task.dueDate) },
    { key: 'createdAt', icon: LuCalendarPlus, label: label('panel.createdAt'), value: formatDateTime(task.createdAt) },
    { key: 'createdBy', icon: LuUserPen, label: label('panel.createdBy'), value: creatorLabel(task.createdBy) },
    task.completedAt && { key: 'completedAt', icon: LuCalendarCheck, label: label('panel.completedAt'), value: formatDateTime(task.completedAt) },
  ].filter(Boolean);

  let footer;
  if (canChange) {
    footer = completed
      ? { label: label('panel.reopen'), title: label('panel.reopenHint'), icon: LuRotateCcw, tone: 'danger', onClick: () => onStatusChange(task.id, 'pending') }
      : { label: label('panel.markDone'), icon: LuCircleCheck, tone: 'success', onClick: () => onStatusChange(task.id, 'completed') };
  }

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={task.id}
      identity={{
        avatar: <LuListTodo aria-hidden="true" className="tw-h-7 tw-w-7" />,
        gradient: statusMeta(task.status).gradient,
        title: task.title || '-',
        badges: (
          <>
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} long />
            {overdue && <OverdueBadge />}
            {task.autoCreated && (
              <span title={label('panel.autoHint')} className={`${TAG} tw-bg-violet-500/10 tw-text-violet-700 tw-ring-violet-500/25 dark:tw-text-violet-300`}>
                <LuSparkles aria-hidden="true" className="tw-h-3 tw-w-3" />
                {label('panel.auto')}
              </span>
            )}
            {task.isAccountingTask && (
              <span title={label('panel.accountingHint')} className={`${TAG} tw-bg-amber-500/10 tw-text-amber-700 tw-ring-amber-500/25 dark:tw-text-amber-300`}>
                <LuWallet aria-hidden="true" className="tw-h-3 tw-w-3" />
                {label('panel.accounting')}
              </span>
            )}
          </>
        ),
      }}
      actions={isAdmin ? [
        { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
        { key: 'delete', label: label('panel.delete'), icon: LuTrash2, tint: 'rose', onClick: onDelete },
      ] : []}
      details={details}
      footer={footer}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      <section aria-label={label('panel.description')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.description')}</p>
        <p className={`tw-whitespace-pre-wrap tw-break-words tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-relaxed dark:tw-border-white/10 dark:tw-bg-white/[0.03] ${task.description ? 'tw-text-slate-700 dark:tw-text-slate-200' : 'tw-italic tw-text-slate-400'}`}>
          {task.description || label('panel.noDescription')}
        </p>
      </section>

      {assignees.length > 0 && (
        <section aria-label={label('panel.assignees')} className="tw-px-1">
          <p className={SECTION_TITLE}>{label('panel.assignees')} · {assignees.length}</p>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-px-1">
            {assignees.map((name) => (
              <span key={name} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-900/[0.05] tw-py-1 tw-pl-1 tw-pr-2.5 tw-text-xs tw-font-semibold tw-text-slate-700 dark:tw-bg-white/[0.07] dark:tw-text-slate-200">
                <span aria-hidden="true" className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-[9px] tw-font-bold tw-text-white">
                  {String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toLocaleUpperCase('tr-TR')}
                </span>
                {name}
              </span>
            ))}
          </div>
        </section>
      )}

      <section aria-label={label('panel.statusTitle')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.statusTitle')}</p>
        {canChange
          ? <StatusChooser status={task.status || 'pending'} busy={savingStatus} onChange={(value) => onStatusChange(task.id, value)} />
          : <p className="tw-px-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('panel.readOnly')}</p>}
      </section>
    </DetailPane>
  );
};

TaskDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  task: PropTypes.object,
  isAdmin: PropTypes.bool,
  canChange: PropTypes.bool,
  savingStatus: PropTypes.bool,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default TaskDetailPane;
